// Unpublished Work © 2024 Deere & Company.
import {
  DeprecatedDropdown,
  DeprecatedDropdownButton,
  DeprecatedDropdownItem,
  DeprecatedDropdownMenu,
} from "@deere/fuel-react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PowerBIModal } from "./PowerBIModal";
import { RBACRole } from "../../../../api/rbac/types";
import { getPowerBIEmbedInfo } from "../../../../api/reports/PowerbiService";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { UserContext } from "../../../../context/UserContext/UserContext";

const CoreMenu = ({
  onPowerBIClick,
  user,
}: {
  onPowerBIClick: () => void;
  user: any;
}) => {
  const { t } = useTranslation("common");
  const theme = useTheme();

  const hasPromotionTrackerRole = user?.is(RBACRole.PROMOTION_TRACKER);

  const dropDownItemStyle = {
    borderWidth: "0px 0px 0px 3px",
    borderStyle: "solid",
    borderColor: "transparent",
  };

  const dropDownHoverStyle = {
    ".MenuItem-link:hover": {
      color: theme.tokens.FuelColorBrandPrimary,
      backgroundColor: theme.tokens.FuelColorAlertSuccessBackground,
      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
    },
  };

  const handlePowerBIClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPowerBIClick();
  };

  return (
    <Box sx={dropDownHoverStyle}>
      {hasPromotionTrackerRole && (
        <DeprecatedDropdownItem
          style={dropDownItemStyle}
          data-testid="ppt-reports-dropdownitem"
          onClick={handlePowerBIClick}
        >
          {t("header.navItem.reports.pptReports")}
        </DeprecatedDropdownItem>
      )}
    </Box>
  );
};

export const ReportsMenu = ({ selected = false }: { selected?: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { authState } = useOktaAuth();
  const user = useContext(UserContext);
  const [showPowerBI, setShowPowerBI] = useState(false);
  const [powerbiEmbedUrl, setPowerbiEmbedUrl] = useState<string | null>(null);
  const [powerbiToken, setPowerbiToken] = useState<string | null>(null);
  const [isLoadingPowerBI, setIsLoadingPowerBI] = useState(false);
  const hasFetchedRef = useRef(false);
  const inFlightRef = useRef(false);
  const { dealerAccount } = useContext(DealerAccountContext);
  const dealerAcctId = dealerAccount?.id;

  const {
    PPT_REPORTS_BACKEND_URL,
    PPT_POWERBI_WORKSPACE_ID,
    PPT_POWERBI_REPORT_ID,
    PPT_POWERBI_DATASET_ID,
    PPT_ROLES,
  } = useContext(ConfigContext);

  async function loadPowerBI(
    showModalOnSuccess: boolean,
    forceRefetch = false
  ) {
    if (inFlightRef.current) {
      if (showModalOnSuccess) setShowPowerBI(true);
      return;
    }
    if (hasFetchedRef.current && !forceRefetch) {
      if (showModalOnSuccess) setShowPowerBI(true);
      return;
    }

    inFlightRef.current = true;
    setIsLoadingPowerBI(true);

    try {
      const jwtToken = authState?.accessToken?.accessToken;

      const result = await getPowerBIEmbedInfo({
        reportsURL: PPT_REPORTS_BACKEND_URL,
        workspaceId: PPT_POWERBI_WORKSPACE_ID,
        datasetId: PPT_POWERBI_DATASET_ID,
        reportId: PPT_POWERBI_REPORT_ID,
        jwtToken: jwtToken ?? "",
        dealerAcctId: dealerAcctId ?? "",
        roles: PPT_ROLES,
      });

      if (result?.token) {
        setPowerbiEmbedUrl(result.embedUrl || null);
        setPowerbiToken(result.token);
        hasFetchedRef.current = true;
      }

      if (showModalOnSuccess) {
        setShowPowerBI(true);
      }
    } catch {
      setShowPowerBI(true);
      setPowerbiEmbedUrl(null);
      setPowerbiToken(null);
    } finally {
      inFlightRef.current = false;
      setIsLoadingPowerBI(false);
    }
  }

  const handlePowerBIClick = () => {
    loadPowerBI(true).then((r) => r);
  };

  const handleClosePowerBI = () => {
    setShowPowerBI(false);
    setPowerbiEmbedUrl(null);
    setPowerbiToken(null);
    setIsLoadingPowerBI(false);
    hasFetchedRef.current = false;
  };

  return (
    <>
      <DeprecatedDropdown>
        <DeprecatedDropdownButton
          variant="text"
          data-testid="reports-drop-down"
          style={{
            padding: "20px 8px",
            color: theme.tokens.FuelColorGray600,
            borderRadius: "0px",
            ...(selected && {
              color: theme.tokens.FuelColorBrandPrimary,
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              borderBottom: `3px solid ${theme.tokens.FuelColorBrandPrimary}`,
            }),
          }}
        >
          {t("header.navItem.reports")}
        </DeprecatedDropdownButton>
        <DeprecatedDropdownMenu
          className="reports-menu-dropdown"
          style={{ minWidth: "max-content", zIndex: 1000 }}
        >
          <Box sx={{ display: "flex" }}>
            <CoreMenu onPowerBIClick={handlePowerBIClick} user={user} />
          </Box>
        </DeprecatedDropdownMenu>
      </DeprecatedDropdown>

      <PowerBIModal
        showModal={showPowerBI}
        handleClose={handleClosePowerBI}
        embedUrl={powerbiEmbedUrl || null}
        accessToken={powerbiToken || null}
        isLoading={isLoadingPowerBI}
        title={t("header.navItem.report.ppt.title")}
        onRetry={() => loadPowerBI(true)}
      />
    </>
  );
};
